import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Modal, message, Alert } from 'antd';
import { scApi, FC110 } from '../../services/scApi';
import { auditLogApi } from '../../services/auditLogApi';
import { useSCMode } from './hooks/useSCMode';
import { useLookupNames } from './hooks/useLookupNames';
import { useSCRecord } from './hooks/useSCRecord';
import { useSCNavigation } from './hooks/useSCNavigation';
import { useSCPermissions } from './hooks/useSCPermissions';
import { useSCApproval } from './hooks/useSCApproval';
import PermissionGuard from '../../components/PermissionGuard';
import { Modules } from '../../constants/permissions';
import TopSearchBar from './components/TopSearchBar';
import Tab1BasicInfo from './components/Tab1BasicInfo';
import Tab2ProductDetail from './components/Tab2ProductDetail';
import Tab3Extra from './components/Tab3Extra';
import { SharedActionBar, ApprovalState } from '../../components/SharedActionBar';
import PrintReportModal from './components/PrintReportModal';
import ApprovalHistoryModal from './components/ApprovalHistoryModal';
import { getStatusBadge, isFieldDisabledFn } from './helpers';
import {
  AcceptanceDataPanel,
  AcceptanceToolbar,
  buildSCAcceptanceDraft,
  pwc202f1AcceptanceManifest,
  useAcceptanceMode,
} from '../../features/acceptance';
import './SCForm.css';

// ── Component ────────────────────────────────────────────────
const SCForm: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const reviewScNo = searchParams.get('review');
  const [activeTab, setActiveTab] = useState(0);
  const [acceptanceLatest, setAcceptanceLatest] = useState<FC110[]>([]);
  const [acceptanceHistory, setAcceptanceHistory] = useState<unknown[]>([]);
  const [acceptanceAudit, setAcceptanceAudit] = useState<unknown[]>([]);
  const [acceptanceDatabaseRecord, setAcceptanceDatabaseRecord] = useState<FC110 | null>(null);
  const [acceptanceErrors, setAcceptanceErrors] = useState<string[]>([]);
  const [acceptanceLoading, setAcceptanceLoading] = useState(false);
  const importData = (location.state as { importData?: FC110 } | null)?.importData;
  const acceptanceMode = useAcceptanceMode();
  const acceptanceEnabled = acceptanceMode.isEnabled(pwc202f1AcceptanceManifest.formId);

  // ── Hooks ──
  const perms = useSCPermissions();
  const { mode, setMode, isEditing } = useSCMode();
  const { names, resolveAllNames, resolveSingleName, clearNames } = useLookupNames();
  const {
    record, lastApiResponse, formData, details, selectedRow, loading,
    setSelectedRow, setDetails,
    loadRecord, setField, getField,
    getDetailField, setDetailField,
    handleSave, handleDelete,
    addDetailRow, deleteDetailRow,
    revertToRecord, resetForAdd, applyResult, applyImportData,
    getFieldError,
  } = useSCRecord();

  // Wrap loadRecord to also resolve names
  const loadRecordWithNames = useCallback(async (scNo: string) => {
    const result = await loadRecord(scNo);
    if (result) {
      resolveAllNames(result.data, result.dets[0]);
    }
  }, [loadRecord, resolveAllNames]);

  const { allIds, currentIdx, setCurrentIdx, loadList, navTo } = useSCNavigation(loadRecordWithNames);

  // ── Initial Load ──
  useEffect(() => {
    (async () => {
      try {
        const ids = await loadList();
        if (reviewScNo) {
          // 從待審核清單跳轉進來 — 自動載入該筆記錄
          const idx = ids.indexOf(reviewScNo);
          if (idx >= 0) {
            setCurrentIdx(idx);
          }
          await loadRecordWithNames(reviewScNo);
        } else if (importData) {
          setCurrentIdx(-1);
          setMode('add');
          setActiveTab(0);
          clearNames();
          applyImportData(importData);
          resolveAllNames(importData, importData.details?.[0]);
        } else if (ids.length > 0) {
          setCurrentIdx(0);
          await loadRecordWithNames(ids[0]);
        }
      } catch {
        console.error('Failed to load SC list');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadList, loadRecordWithNames, setCurrentIdx, reviewScNo, importData, applyImportData, setMode, clearNames, resolveAllNames]);

  // ── Resolve detail-level names when selected row changes ──
  useEffect(() => {
    const d = details[selectedRow];
    if (d) {
      Promise.all([
        scApi.lookupName('packing', d.fc111_30 || ''),
        scApi.lookupName('tradeTerms', d.fc111_29 || ''),
        scApi.lookupName('currency', d.fc111_41 || ''),
      ]).then(([packing, tradeTerms, purchCurrency]) => {
        // Use functional update via the exposed setNames — but we use resolveSingleName
        // pattern instead. For batch, we call them individually:
        resolveSingleName('packing', 'packing', d.fc111_30 || '');
        resolveSingleName('tradeTerms', 'tradeTerms', d.fc111_29 || '');
        resolveSingleName('purchCurrency', 'currency', d.fc111_41 || '');
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRow, details]);

  // ── Mode Actions ──
  const handleAddAction = useCallback(async () => {
    if (isEditing) {
      // Save
      await handleSave(mode, async (result: FC110) => {
        if (mode === 'add') {
          const ids = await loadList();
          const newIdx = ids.indexOf(result.fc110_01);
          setCurrentIdx(newIdx >= 0 ? newIdx : 0);
        }
        applyResult(result);
        setMode('browse');
      });
    } else {
      // Enter add mode
      setMode('add');
      resetForAdd();
      setActiveTab(0);
      clearNames();
    }
  }, [isEditing, mode, handleSave, loadList, setCurrentIdx, applyResult, setMode, resetForAdd, clearNames]);

  const handleModifyAction = useCallback(() => {
    if (isEditing) {
      // Revert
      revertToRecord();
      setMode('browse');
    } else {
      if (!record) return;
      setMode('edit');
    }
  }, [isEditing, record, revertToRecord, setMode]);

  const handleDeleteAction = useCallback(() => {
    if (!record || isEditing) return;
    Modal.confirm({
      title: '確認刪除',
      content: `確定要刪除 S/C ${record.fc110_01}？`,
      okText: '刪除',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        const success = await handleDelete();
        if (success) {
          const ids = await loadList();
          if (ids.length > 0) {
            const nextIdx = Math.min(currentIdx, ids.length - 1);
            setCurrentIdx(nextIdx);
            await loadRecordWithNames(ids[nextIdx]);
          } else {
            resetForAdd();
            setCurrentIdx(-1);
          }
        }
      },
    });
  }, [record, isEditing, handleDelete, loadList, currentIdx, setCurrentIdx, loadRecordWithNames, resetForAdd]);

  const handleNavTo = useCallback((idx: number) => {
    navTo(idx, isEditing);
  }, [navTo, isEditing]);

  // Copy handler (BR-D02)
  const handleCopy = useCallback(() => {
    if (!record || isEditing) return;
    Modal.confirm({
      title: '確認複製',
      content: `確定要複製 S/C ${record.fc110_01}？`,
      okText: '複製',
      cancelText: '取消',
      onOk: async () => {
        try {
          const result = await scApi.copy(record.fc110_01);
          message.success(`已複製為 ${result.fc110_01}`);
          const ids = await loadList();
          const newIdx = ids.indexOf(result.fc110_01);
          setCurrentIdx(newIdx >= 0 ? newIdx : 0);
          await loadRecordWithNames(result.fc110_01);
        } catch {
          message.error('複製失敗');
        }
      },
    });
  }, [record, isEditing, loadList, setCurrentIdx, loadRecordWithNames]);

  // Search handler — TopSearchBar 選取搜尋結果時觸發
  const handleSearchSelect = useCallback(async (scNo: string) => {
    // Find in allIds and navigate, or load directly
    const idx = allIds.indexOf(scNo);
    if (idx >= 0) {
      setCurrentIdx(idx);
      await loadRecordWithNames(scNo);
    } else {
      // Not in current list (possibly filtered), load directly
      await loadRecordWithNames(scNo);
      // Reload list to include it
      const ids = await loadList();
      const newIdx = ids.indexOf(scNo);
      if (newIdx >= 0) setCurrentIdx(newIdx);
    }
  }, [allIds, setCurrentIdx, loadRecordWithNames, loadList]);

  // Print handler (BR-F17) — 開啟列印選項 Modal
  const [printModalVisible, setPrintModalVisible] = useState(false);
  const handlePrint = useCallback(() => {
    if (!record || isEditing) return;
    setPrintModalVisible(true);
  }, [record, isEditing]);

  // ── Approval Workflow (BR-U11 / BR-U03) ──
  const {
    approvalHistoryVisible, setApprovalHistoryVisible,
    handleSubmit, handleApprove, handleReject, handleWriteoff, handleViewHistory,
  } = useSCApproval({ record, isEditing, reviewScNo, loadRecordWithNames });

  // ── Computed Values ──
  const salesAmt = formData.fc110_32 || 0;
  const purchaseAmt = formData.fc110_33 || 0;
  const expenseAmt = formData.fc110_35 || 0;
  const commIncome = formData.fc110_37 || 0;
  const commExpense = formData.fc110_36 || 0;
  const processingFee = formData.fc110_34 || 0;
  const grossProfit = salesAmt - purchaseAmt - expenseAmt + commIncome - commExpense - processingFee;
  const grossProfitRate = salesAmt ? (grossProfit / salesAmt) * 100 : 0;

  const selDetail = details[selectedRow] as Partial<import('../../services/scApi').FC111> | undefined;
  const detailUnitProfitRate = (() => {
    if (!selDetail?.fc111_15 || !formData.fc110_25) return '0.00';
    const denom = selDetail.fc111_15 * formData.fc110_25;
    if (!denom) return '0.00';
    return ((selDetail.fc111_51 || 0) / denom * 100).toFixed(2);
  })();

  const statusBadge = getStatusBadge(record);
  const isFieldDisabled = useCallback((key: string) => isFieldDisabledFn(key, isEditing, mode), [isEditing, mode]);

  const loadAcceptanceData = useCallback(async () => {
    if (!acceptanceEnabled) return;
    setAcceptanceLoading(true);
    const [latest, history, database, audit] = await Promise.allSettled([
      scApi.list(1, 3),
      record?.fc110_01 ? scApi.getApprovalHistory(record.fc110_01) : Promise.resolve([]),
      record?.fc110_01 ? scApi.get(record.fc110_01) : Promise.resolve(null),
      auditLogApi.list({ form_id: pwc202f1AcceptanceManifest.formId, doc_key: record?.fc110_01, page: 1, pageSize: 20 }),
    ]);
    setAcceptanceLatest(latest.status === 'fulfilled' ? latest.value.data || [] : []);
    setAcceptanceHistory(history.status === 'fulfilled' ? history.value : []);
    setAcceptanceDatabaseRecord(database.status === 'fulfilled' ? database.value : null);
    setAcceptanceAudit(audit.status === 'fulfilled' ? audit.value.data || [] : []);
    setAcceptanceErrors([
      ...(latest.status === 'rejected' ? ['最新三筆 API 讀取失敗'] : []),
      ...(history.status === 'rejected' ? ['審核歷程 API 讀取失敗'] : []),
      ...(database.status === 'rejected' ? ['DB 即時單據 API 讀取失敗'] : []),
      ...(audit.status === 'rejected' ? ['SYS_AuditLog 讀取失敗或目前使用者無權限'] : []),
    ]);
    setAcceptanceLoading(false);
  }, [acceptanceEnabled, record?.fc110_01]);

  useEffect(() => {
    void loadAcceptanceData();
  }, [loadAcceptanceData]);

  const handleAcceptanceAutoFill = useCallback(() => {
    if (!record) {
      message.warning('請先開啟一筆完整的既有 S/C；驗收模式只建立未儲存草稿。');
      return;
    }
    const preview = buildSCAcceptanceDraft(record);
    setMode('add');
    applyImportData(preview);
    setActiveTab(0);
    message.success('已建立未儲存的 S/C 驗收草稿；儲存仍走原本 SC API 與全部驗證。');
  }, [applyImportData, record, setMode]);

  // ── Render ─────────────────────────────────────────────────
  return (
    <PermissionGuard module={Modules.SALES_CONTRACT} action="query">
    <div className="sc-form">
      {loading && <div className="loading-overlay">載入中...</div>}

      {reviewScNo && (
        <Alert
          message="您正在審核此單據"
          description={`單號: ${reviewScNo}。核准或退回後將自動返回待審核清單。`}
          type="warning"
          showIcon
          banner
          style={{ marginBottom: 0 }}
        />
      )}

      <div className="page-wrapper">

        {/* Page Title Bar (breadcrumb + title merged) */}
        <div className="page-title-bar">
          <div className="page-title-left">
            <nav className="breadcrumb-inline">
              <a href="#/erp" onClick={e => { e.preventDefault(); navigate('/erp'); }}>首頁</a>
              <span>/</span>
              <a href="#/erp" onClick={e => { e.preventDefault(); navigate('/erp'); }}>訂單管理</a>
              <span>/</span>
              <span className="current">SC建檔</span>
            </nav>
            <span className="title-separator">—</span>
            <h1>銷售合約 Sales Confirmation</h1>
            <span className="badge badge-default">C2021</span>
            {statusBadge && (
              <span className={`badge ${statusBadge.cls}`}>{statusBadge.text}</span>
            )}
          </div>
          <div className="page-title-right">
            <button className="btn btn-sm" title="審核歷史" disabled={!record} onClick={handleViewHistory}>審核歷史</button>
            <button className="btn btn-sm" title="送審" disabled={!record || isEditing} onClick={handleSubmit}>送審檢查</button>
          </div>
        </div>

        <AcceptanceToolbar
          manifest={pwc202f1AcceptanceManifest}
          onAutoFill={handleAcceptanceAutoFill}
          documentKey={record?.fc110_01}
          autoFillDisabled={!record || isEditing}
          autoFillReason={!record ? '請先開啟一筆可作為範例的既有 S/C' : isEditing ? '請先儲存或取消目前編輯' : '複製目前 S/C 的業務欄位，建立不落庫草稿'}
        />

        {/* ===== Search Bar + Form Info Bar ===== */}
        <TopSearchBar
          formData={formData}
          isEditing={isEditing}
          isFieldDisabled={isFieldDisabled}
          getField={getField}
          setField={setField}
          allIds={allIds}
          onNavTo={handleNavTo}
          onSearchSelect={handleSearchSelect}
        />

        {/* ===== Tabs (connected to form-info-bar above) ===== */}
        <div className="tabs">
          <div className="tab-nav">
            {['基本資料', '產品明細', '嘜頭/信用額度'].map((label, i) => (
              <button
                key={i}
                className={`tab-nav-item ${activeTab === i ? 'active' : ''}`}
                onClick={() => setActiveTab(i)}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Tab 1: 基本資料 */}
          <div className={`tab-pane ${activeTab === 0 ? 'active' : ''}`}>
            <Tab1BasicInfo
              formData={formData}
              names={names}
              isEditing={isEditing}
              isFieldDisabled={isFieldDisabled}
              getField={getField}
              setField={setField}
              resolveSingleName={resolveSingleName}
              getFieldError={getFieldError}
              salesAmt={salesAmt}
              purchaseAmt={purchaseAmt}
              expenseAmt={expenseAmt}
              commIncome={commIncome}
              commExpense={commExpense}
              processingFee={processingFee}
              grossProfit={grossProfit}
              grossProfitRate={grossProfitRate}
              documentKey={record?.fc110_01}
            />
          </div>

          {/* Tab 2: 產品明細 */}
          <div className={`tab-pane ${activeTab === 1 ? 'active' : ''}`}>
            <Tab2ProductDetail
              formData={formData}
              details={details}
              selectedRow={selectedRow}
              names={names}
              isEditing={isEditing}
              setSelectedRow={setSelectedRow}
              setDetails={setDetails}
              getDetailField={getDetailField}
              setDetailField={setDetailField}
              resolveSingleName={resolveSingleName}
              addDetailRow={addDetailRow}
              deleteDetailRow={deleteDetailRow}
              detailUnitProfitRate={detailUnitProfitRate}
              documentKey={record?.fc110_01}
            />
          </div>

          {/* Tab 3: 嘜頭/信用額度 */}
          <div className={`tab-pane ${activeTab === 2 ? 'active' : ''}`}>
            <Tab3Extra
              formData={formData}
              names={names}
              isEditing={isEditing}
              getField={getField}
              setField={setField}
              resolveSingleName={resolveSingleName}
              salesAmt={salesAmt}
            />
          </div>

        </div>{/* /tabs */}

        {/* ===== Bottom Action Bar (Pwtcmd1) ===== */}
        {(() => {
          const s51 = (record?.fc110_51 || '').trim();
          const s53 = (record?.fc110_53 || '').trim();
          const s71 = (record?.fc110_71 || '').trim();
          // FC110_51 統一碼：N=草稿/退回/沖銷、D=送審、P=核准（舊碼 S/R 過渡容忍，批次 5）
          const isLocked = s51 === 'P' || s51 === 'D' || s51 === 'S' || s53 === 'Y' || s71 === 'Y';
          const approval: ApprovalState = {
            statusCode: s51,
            isApproved: s51 === 'P',
            isPending: ['D', 'S'].includes(s51), // 統一碼 D + 舊碼 S 過渡容忍
            isLocked,
          };
          // Custom status label for SC (handles closed/cancelled states)
          let statusLabel: string | null = null;
          if (s53 === 'Y') statusLabel = '已結案';
          else if (s71 === 'Y') statusLabel = '已取消';
          else if (s51 === 'P') statusLabel = '已核准';
          else if (['D', 'S'].includes(s51)) statusLabel = '送審中';
          else if (s51 === 'R') statusLabel = '已退回'; // 舊碼殘留顯示容忍（退回現為 N）
          else if ((record?.fc110_91 || '').trim() === 'COPY') statusLabel = '複製';
          else if ((record?.fc110_91 || '').trim() === 'MODI') statusLabel = '修改';
          return (
            <SharedActionBar
              mode={mode} isEditing={isEditing} hasRecord={!!record}
              currentIdx={currentIdx} recordCount={allIds.length}
              onNavFirst={() => handleNavTo(0)} onNavPrev={() => handleNavTo(currentIdx - 1)}
              onNavNext={() => handleNavTo(currentIdx + 1)} onNavLast={() => handleNavTo(allIds.length - 1)}
              onAdd={handleAddAction} onModify={handleModifyAction} onDelete={handleDeleteAction}
              onSave={handleAddAction} onCancel={handleModifyAction}
              approval={approval}
              onSubmit={handleSubmit} onApprove={handleApprove}
              onReject={handleReject} onWriteoff={handleWriteoff} onHistory={handleViewHistory}
              onCopy={handleCopy} onPrint={handlePrint}
              onExit={() => navigate('/erp')}
              canCreate={perms.canCreate} canEdit={perms.canEdit}
              canDelete={perms.canDelete} canApprove={perms.canApprove}
              canPrint={perms.canPrint}
              statusLabel={statusLabel}
            />
          );
        })()}

        <AcceptanceDataPanel
          manifest={pwc202f1AcceptanceManifest}
          documentKey={record?.fc110_01}
          snapshots={{
            screenDraft: { master: formData, details },
            lastApiResponse: lastApiResponse ? { master: lastApiResponse, details: lastApiResponse.details || [] } : null,
            databaseRecord: acceptanceDatabaseRecord ? { master: acceptanceDatabaseRecord, details: acceptanceDatabaseRecord.details || [] } : null,
          }}
          latestRecords={acceptanceLatest}
          history={acceptanceHistory}
          auditEntries={acceptanceAudit}
          loading={acceptanceLoading || loading}
          onRefresh={() => { void loadAcceptanceData(); }}
          observationErrors={acceptanceErrors}
        />

      </div>{/* /page-wrapper */}

      {/* 列印選項 Modal（PWC202FP） */}
      {record && (
        <PrintReportModal
          visible={printModalVisible}
          scNo={record.fc110_01}
          onClose={() => setPrintModalVisible(false)}
        />
      )}

      {/* 審核歷史 Modal（BR-U10） */}
      {record && (
        <ApprovalHistoryModal
          visible={approvalHistoryVisible}
          scNo={record.fc110_01}
          onClose={() => setApprovalHistoryVisible(false)}
        />
      )}
    </div>
    </PermissionGuard>
  );
};

export default SCForm;
